import { Injectable, inject } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { WindowSizeService } from '../shared/window-size.service';

interface PanelMap {
  [key: string]: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PanelService {
  private readonly WindowSizeService = inject(WindowSizeService);
  private readonly panelState = new BehaviorSubject<PanelMap>({});

  public readonly panelState$: Observable<PanelMap> = this.panelState.asObservable();

  private readonly subscription = combineLatest([
    this.panelState$,
    this.WindowSizeService.isMobile$,
  ])
    .pipe(map(([panelState, isMobile]) => panelState || isMobile))
    .subscribe();

  public registerPanel(key: string): void {
    const current = this.panelState.getValue();
    if (!(key in current)) {
      const newValue = { ...current, [key]: false };
      this.panelState.next(newValue);
    }
  }

  public getPanelState(key: string): Observable<boolean> {
    return this.panelState$.pipe(map((panelState: PanelMap) => panelState[key] || false));
  }

  public togglePanel(key: string): void {
    const current = this.panelState.getValue();
    const newValue = { ...current, [key]: !current[key] };
    this.panelState.next(newValue);
  }

  public openPanel(key: string): void {
    const current = this.panelState.getValue();
    const newValue = { ...current, [key]: true };
    this.panelState.next(newValue);
  }

  public closePanel(key: string): void {
    const current = this.panelState.getValue();
    const newValue = { ...current, [key]: false };
    this.panelState.next(newValue);
  }

  public closeAll(): void {
    const current = this.panelState.getValue();
    const newValue = Object.keys(current).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {} as PanelMap);
    this.panelState.next(newValue);
  }

  public cleanup(): void {
    this.subscription?.unsubscribe();
  }
}
